import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    EmptyActionSkeletonConstantType,
    EmptyMediaStyleConstantType,
    EmptyMeasuresConstantType,
    EmptyColorsConstantType,
    EmptyTypeConstantType,
    EmptyDynamicStyleType,
    EmptyActionSkeleton,
    EmptyMeasuresKeys,
    EmptyPaddingKeys,
    EmptyMeasures,
    EmptyVariant,
    EmptyColors,
    EmptyTypes,
    EmptySize,
    EmptyType
} from "./type";
import type {
    Mutable
} from "../../types";
import type {
    ButtonSize
} from "../button/type";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    STATE_COLOR_KEYS
} from "../../helpers/theme/state";
import {
    getButtonXSmallHeight
} from "../button/stylesheet";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const EMPTY_TYPES: Record<EmptyType, EmptyTypes> = {
    neutral: {
        mediaColor: STATE_COLOR_KEYS.neutral,
        actionVariant: "tonal",
        iconColor: "default"
    },
    primary: {
        mediaColor: STATE_COLOR_KEYS.primary,
        actionVariant: "filled",
        iconColor: "emphasized"
    },
    success: {
        mediaColor: STATE_COLOR_KEYS.success,
        actionVariant: "tonal",
        iconColor: "success"
    },
    warning: {
        mediaColor: STATE_COLOR_KEYS.warning,
        actionVariant: "tonal",
        iconColor: "warning"
    },
    danger: {
        mediaColor: STATE_COLOR_KEYS.danger,
        actionVariant: "tonal",
        iconColor: "danger"
    },
    info: {
        mediaColor: STATE_COLOR_KEYS.info,
        actionVariant: "tonal",
        iconColor: "info"
    }
};

export const EMPTY_SIZES: Record<EmptySize, EmptyMeasuresKeys> = {
    medium: {
        descriptionVariant: "bodyMediumSize",
        titleVariant: "titleMediumSize",
        actionGap: "spacingSm",
        headerGap: "spacingMd",
        actionHeight: "small",
        textGap: "spacingXs",
        mediaSize: "small",
        iconSize: "large"
    },
    small: {
        descriptionVariant: "bodySmallSize",
        titleVariant: "titleSmallSize",
        actionGap: "spacingSm",
        headerGap: "spacingSm",
        actionHeight: "small",
        textGap: "spacingXs",
        mediaSize: "xSmall",
        iconSize: "medium"
    }
};

export const EMPTY_GAPS: Record<EmptySize, Record<EmptyVariant, keyof NCoreUIKit.Spaces>> = {
    medium: {
        outline: "spacingLg",
        card: "spacingLg",
        plain: "spacingMd"
    },
    small: {
        outline: "spacingMd",
        plain: "spacingMd",
        card: "spacingMd"
    }
};

export const EMPTY_PADDINGS: Record<EmptySize, Record<EmptyVariant, EmptyPaddingKeys>> = {
    medium: {
        card: {
            horizontal: "spacingXxl",
            vertical: "spacingXxl"
        },
        outline: {
            horizontal: "spacingXxl",
            vertical: "spacingXxl"
        },
        plain: {
            horizontal: "spacingLg",
            vertical: "spacingXl"
        }
    },
    small: {
        card: {
            horizontal: "spacingMd",
            vertical: "spacingLg"
        },
        outline: {
            horizontal: "spacingMd",
            vertical: "spacingLg"
        },
        plain: {
            horizontal: "spacingMd",
            vertical: "spacingLg"
        }
    }
};

export const EMPTY_ACTION_SKELETON_RATIO = 3.5;

export const EMPTY_ACTION_DEFAULT_SIZE: ButtonSize = "medium";

export const EMPTY_DESCRIPTION_SKELETON_WIDTH = "90%";

export const EMPTY_TITLE_SKELETON_WIDTH = "60%";

export const EMPTY_DESCRIPTION_SKELETON_LINES = 2;

export const EMPTY_HEADING_LEVEL = 3;

export const getEmptyType = ({
    type
}: EmptyTypeConstantType): EmptyTypes => {
    return EMPTY_TYPES[type];
};

export const getEmptyMeasures = ({
    controlHeights,
    avatarSizes,
    iconSizes,
    variant,
    spaces,
    size
}: EmptyMeasuresConstantType): EmptyMeasures => {
    const currentSize = EMPTY_SIZES[size];

    const currentPadding = EMPTY_PADDINGS[size][variant];

    return {
        actionHeight: controlHeights[currentSize.actionHeight],
        paddingHorizontal: spaces[currentPadding.horizontal],
        descriptionVariant: currentSize.descriptionVariant,
        paddingVertical: spaces[currentPadding.vertical],
        mediaSize: avatarSizes[currentSize.mediaSize],
        iconSize: iconSizes[currentSize.iconSize],
        headerGap: spaces[currentSize.headerGap],
        actionGap: spaces[currentSize.actionGap],
        titleVariant: currentSize.titleVariant,
        gap: spaces[EMPTY_GAPS[size][variant]],
        textGap: spaces[currentSize.textGap]
    };
};

export const getEmptyColors = ({
    colors,
    type
}: EmptyColorsConstantType): EmptyColors => {
    const currentType = getEmptyType({
        type
    });

    return {
        mediaColor: type === "neutral" ? colors.system.state.overlay.fill.default : colors.system.low[currentType.mediaColor],
        iconColor: colors.content.icon[currentType.iconColor],
        mediaBorderColor: colors.content.border.subtle,
        descriptionColor: colors.content.text.mid,
        iconColorKey: currentType.iconColor,
        titleColor: colors.content.text.high
    };
};

export const getEmptyActionSkeleton = ({
    controlHeights,
    shapeConfigs,
    touchTargets,
    actionSizes,
    measures
}: EmptyActionSkeletonConstantType): EmptyActionSkeleton => {
    const height = actionSizes.length ? Math.max(...actionSizes.map((actionSize) => actionSize === "xSmall" ? getButtonXSmallHeight({
        controlHeights,
        shapeConfigs,
        touchTargets
    }) : controlHeights[actionSize])) : measures.actionHeight;

    return {
        width: height * EMPTY_ACTION_SKELETON_RATIO,
        height
    };
};

export const getEmptyMediaStyle = ({
    shapeConfigs,
    measures,
    radiuses,
    borders,
    colors
}: EmptyMediaStyleConstantType): ViewStyle => {
    return {
        ...getCornerStyle({
            radius: radiuses.actions,
            shapeConfigs
        }),
        borderColor: colors.mediaBorderColor,
        backgroundColor: colors.mediaColor,
        height: measures.mediaSize,
        width: measures.mediaSize,
        borderWidth: borders.line
    };
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center"
    },
    content: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "stretch",
        flexWrap: "wrap"
    },
    media: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    contentItem: {
        justifyContent: "center",
        alignItems: "center"
    },
    contentStack: {
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "stretch"
    },
    header: {
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "stretch"
    },
    headerText: {
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "stretch"
    },
    description: {
        textAlign: "center"
    },
    title: {
        textAlign: "center"
    }
});

export const useStyles = ({
    currentMeasures,
    shapeConfigs,
    shadowAlpha,
    elevations,
    radiuses,
    borders,
    variant,
    colors
}: EmptyDynamicStyleType) => {
    const styles = {
        container: {
            paddingHorizontal: currentMeasures.paddingHorizontal,
            paddingVertical: currentMeasures.paddingVertical,
            gap: currentMeasures.gap
        } as Mutable<ViewStyle>
    };

    if(variant === "outline") {
        Object.assign(styles.container, {
            borderColor: colors.content.border.default,
            borderWidth: borders.line,
            borderStyle: "dashed"
        }, getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }));
    }

    if(variant === "card") {
        Object.assign(styles.container, getSurfaceStyle({
            surface: "card",
            shadowAlpha,
            elevations,
            borders,
            colors
        }), getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }));
    }

    return styles;
};
export default stylesheet;
